import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ArrowLeft } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils/cn";
import type { ApiError } from "@/lib/api/unwrap";
import { usePendingReviews, usePublish, useReviewHtml } from "../api";
import { LivePagePreview } from "./LivePagePreview";
import { ActionsStep } from "./ActionsStep";

/**
 * Validator review flow: pick a communication from the **pending-validations
 * list** (enterprises whose latest approval request is newer than their latest
 * publication), review the assembled showcase — editing the member's
 * objectives and actions if need be — and publish it. Mounted only for users with the
 * `communications` capability (see index.tsx) — `can_review_communications`,
 * the validator flag or a superuser, the gate of the Django review views
 * (`ValidatorRequiredMixin`) and of the review API (`IsValidator`). A member link
 * (`?review=<id>`) still deep-links straight to a specific review.
 */
export function ReviewFlow({ initialProfileId = "" }: { initialProfileId?: string }) {
  const { t } = useTranslation();
  const [profileId, setProfileId] = useState(initialProfileId);

  const pending = usePendingReviews();
  const query = useReviewHtml(profileId);
  const publish = usePublish();

  function onPublish() {
    publish.mutate(profileId, {
      onSuccess: () => {
        toast.success(t("communications.review.published"));
        setProfileId("");
      },
      // The API refuses a profile that has no showcase address, and says why.
      // Show that rather than the generic failure, so the validator learns the
      // enterprise has to be renamed first -- the same words the Django app
      // puts in its error banner.
      onError: (error) => {
        // TanStack types the mutation error as Error; unwrap() actually throws
        // an ApiError, so go through unknown to read the reason off it.
        const detail = (error as unknown as ApiError | undefined)?.detail;
        toast.error(detail ?? t("communications.review.publishError"));
      },
    });
  }

  const error = query.error as (Error & { status?: number }) | null;

  const dateFmt = (iso: string) => new Date(iso).toLocaleDateString();

  return (
    <div className="space-y-6">
      {!profileId ? (
        <Card>
          <CardHeader>
            <CardTitle>{t("communications.review.title")}</CardTitle>
            <CardDescription>
              {t("communications.review.pendingDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {pending.isPending ? (
              <p className="text-muted-foreground">{t("communications.loading")}</p>
            ) : pending.isError ? (
              <p className="text-destructive">
                {t("communications.review.loadError")}
              </p>
            ) : (pending.data ?? []).length === 0 ? (
              <p className="text-muted-foreground">
                {t("communications.review.noPending")}
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {pending.data!.map((r) => (
                  <li key={r.enterprise_profile_id}>
                    <button
                      type="button"
                      onClick={() => setProfileId(r.enterprise_profile_id)}
                      className="flex w-full items-center justify-between gap-3 py-3 text-left hover:bg-accent/50"
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-medium">
                          {r.enterprise_name}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {t("communications.review.requestedOn", {
                            date: dateFmt(r.requested_at),
                          })}
                        </span>
                      </span>
                      <span
                        className={cn(
                          "shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium",
                          r.status === "new"
                            ? "bg-primary/10 text-primary"
                            : "bg-amber-500/15 text-amber-700 dark:text-amber-400",
                        )}
                      >
                        {r.status === "new"
                          ? t("communications.review.statusNew")
                          : t("communications.review.statusResubmitted")}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5"
            onClick={() => setProfileId("")}
          >
            <ArrowLeft className="size-4" aria-hidden />
            {t("communications.review.backToList")}
          </Button>
          {/* The member's actions step, as the Django review page reuses the
              member's page: a validator edits what the member would. */}
          <ActionsStep memberId={profileId} />
          <Card>
          <CardHeader>
            <CardTitle>{t("communications.preview.title")}</CardTitle>
          </CardHeader>
          <CardContent>
            {query.isPending ? (
              <p className="text-muted-foreground">{t("communications.loading")}</p>
            ) : query.isError ? (
              <p className="text-destructive">
                {error?.status === 403
                  ? t("communications.review.forbidden")
                  : error?.status === 404
                    ? t("communications.review.notFound")
                    : t("communications.review.loadError")}
              </p>
            ) : query.data ? (
              <LivePagePreview
                html={query.data}
                title={t("communications.review.iframeTitle")}
              />
            ) : null}
          </CardContent>
          <CardFooter className="justify-end">
            <Button
              onClick={onPublish}
              disabled={publish.isPending || !query.isSuccess}
            >
              {publish.isPending
                ? t("communications.review.publishing")
                : t("communications.review.publish")}
            </Button>
          </CardFooter>
          </Card>
        </div>
      )}
    </div>
  );
}
